import React, { memo } from "react";
import { Box, Card, Heading, Stack, Text } from "@origin-digital/ods-core";
import { IconError } from "@origin-digital/ods-icons";
import IconSolarEnergyGenerated from "@/assets/solar_generating.svg";
import { IISolarBatteryOutlookResponse } from "@/contract/generated/electrify-v1";
import { formatLabel } from "@/utils";
import { solarBatteryOutlook as content } from "../content.json";
import IconBatteryCharging from "@/assets/battery_charging.svg";
import IconDaily from "@/assets/daily.svg";
import IconBatteryError from "@/assets/battery_error.svg";
import { isOutlier } from "../utils";

type SummaryProps = {
  solarBatterySystem: IISolarBatteryOutlookResponse;
};

export const Summary = memo(({ solarBatterySystem }: SummaryProps) => {
  const { label, testId } = content;

  const {
    averageDailyGenerationAccurassi,
    percentageDailyConsumption,
    batterySize,
  } = solarBatterySystem;

  const isNoCover =
    percentageDailyConsumption && percentageDailyConsumption < 70;
  const isNotEnoughCover =
    percentageDailyConsumption &&
    percentageDailyConsumption >= 70 &&
    percentageDailyConsumption < 100;
  const isOutlierCover = isOutlier({ solarBatterySystem });

  let batterySummaryTitle = content.summary.canCharge;
  let coverSummaryTitle = content.summary.thatCovers;
  let batterySummarySubTitle = label.overviewHeading.battery;
  let coverSummarySubTitle = <>{label.overviewHeading.payFotItself}</>;
  let batteryIcon = (
    <Box marginBottom={"none"} marginTop={"xxsmall"}>
      <IconBatteryCharging color="secondary" />
    </Box>
  );
  let coverIcone = (
    <>
      <IconDaily />
    </>
  );

  if (isNotEnoughCover) {
    batterySummaryTitle = content.summary.notEnough;
    coverSummaryTitle = content.summary.thatCovers;
    batterySummarySubTitle = content.summary.toChargeBattery;
    coverSummarySubTitle = (
      <>
        {" "}
        {formatLabel(
          label.overviewHeading.payFotItselfNotEnough,
          (value) => value,
          `${percentageDailyConsumption}%`
        )}
      </>
    );
    batteryIcon = (
      <Box marginBottom={"none"} marginTop={"xxsmall"}>
        <IconBatteryError data-id="icon-battery-error" />
      </Box>
    );
    coverIcone = (
      <>
        <IconDaily />
      </>
    );
  }

  if (isNoCover) {
    batterySummaryTitle = content.summary.notEnough;
    coverSummaryTitle = content.summary.mayNotCover;
    batterySummarySubTitle = content.summary.toChargeBattery;
    coverSummarySubTitle = <>{label.overviewHeading.payFotItself}</>;
    batteryIcon = (
      <Box marginBottom={"none"} marginTop={"xsmall"}>
        <IconBatteryError />
      </Box>
    );
    coverIcone = (
      <Box marginBottom={"xsmall"} marginTop={"xsmall"}>
        <IconError color="critical" />
      </Box>
    );
  }

  if (isOutlierCover) {
    batterySummaryTitle = content.summary.canCharge;
    coverSummaryTitle = content.summary.thatCovers;
    batterySummarySubTitle = `a ${label.overviewHeading.battery}`;
    coverSummarySubTitle = <>{label.overviewHeading.payFotItself}</>;
    batteryIcon = (
      <Box marginBottom={"xxsmall"} marginTop={"xxsmall"}>
        <IconBatteryCharging
          color="secondary"
          data-id="icon-battery-charging"
        />
      </Box>
    );
    coverIcone = (
      <>
        <IconDaily />
      </>
    );
  }

  return (
    <Stack space="medium" data-id={testId.summary}>
      <Box style={{ gap: "8px" }} display="flex" paddingX="none">
        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card outline paddingXSpecific="none" paddingYSpecific="none">
            <Box padding={["xsmall", "small"]}>
              <Stack space="xsmall" alignX="center">
                <Text variant="body-small" weight="medium" tone="neutralDark">
                  {content.summary.generating}
                </Text>
                <Box marginBottom={"none"} marginTop={"xsmall"}>
                  <IconSolarEnergyGenerated color="secondary" />
                </Box>
                <Stack space="none" alignX="center">
                  <Heading variant="h4" weight="bold" align="center">
                    {`${averageDailyGenerationAccurassi} kWh`}
                  </Heading>
                  <Text variant="body-small" align="center">
                    {label.overviewHeading.generatedForYou}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>

        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card
            outline
            paddingXSpecific="none"
            paddingYSpecific="none"
            backgroundColor={
              isNotEnoughCover || isNoCover ? "critical" : "white"
            }
          >
            <Box padding={["xsmall", "small"]}>
              <Stack space="xsmall" alignX="center">
                <Text
                  variant="body-small"
                  weight="medium"
                  tone="neutralDark"
                  align="center"
                >
                  {batterySummaryTitle}
                </Text>
                {batteryIcon}
                <Stack space="none" alignX="center">
                  {!isNotEnoughCover && !isNoCover && !isOutlierCover && (
                    <Heading variant="h4" weight="bold">
                      {`${batterySize} kWh`}
                    </Heading>
                  )}
                  <Text variant="body-small" align="center">
                    {batterySummarySubTitle}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>

        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card
            outline
            paddingXSpecific="none"
            paddingYSpecific="none"
            backgroundColor={isNoCover ? "critical" : "white"}
          >
            <Box padding={["xsmall", "small"]}>
              <Stack space="xsmall" alignX="center">
                <Text
                  variant="body-small"
                  weight="medium"
                  tone="neutralDark"
                  align="center"
                >
                  {coverSummaryTitle}
                </Text>
                {coverIcone}
                <Stack space="none" alignX="center">
                  <Text variant="body-small" align="center">
                    {coverSummarySubTitle}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Box>
    </Stack>
  );
});
